// Product Search — Supplier Catalogue Browse.
//
// Source-of-truth: /var/www/kitchenos/portal-standalone.html
//   Page markup           : lines 2638–2663   (#page-product-search)
//   CSS styles            : lines 32773–32897 (see ./styles.css — ported verbatim)
//   JS module IIFE        : lines 32898–33119 (loadProductSearch / renderProductSearch
//                                              / resetProductSearch + openProductDetail)
//   End marker            : line 33120
//
// Backend: /var/www/kitchenos-api/modules/door_ranges.js
//   GET /api/door-ranges?status=approved → { ranges: DoorRange[] }
//
// Steve's task spec said "wire to /api/catalogue/search" but source-of-truth
// uses /api/door-ranges. Steve has approved using /api/door-ranges to match
// source verbatim — the catalogue/search endpoint has a thinner QB-shape
// unsuitable for the rich card-grid Product Search UI.
//
// This module ports the source IIFE verbatim for markup, class names, inline
// styles, copy, filter behaviour, eight category pills, and detail modal.
// Source's global call pattern (oninput="renderProductSearch()" etc.) is
// replaced with addEventListener — modular pages forbid window globals.
// The DOM the user sees is identical.
//
// Departures: none beyond the event-wiring switch above. AbortController is
// added so cleanup cancels an in-flight fetch (source had no cleanup hook).

import "./styles.css";

import { registerPage } from "../../shell/router";
import type { Page, PageContext, PageCleanup } from "../../shell/types";

// ── Types matching /var/www/kitchenos-api/modules/door_ranges.js shape ──────

interface PortfolioImage {
  url: string;
  colourName: string;
}

interface Colour {
  name: string;
  hex: string;
  availability: "Ex-Stock" | "Made to Order" | "Stocked";
  frontalImage: string;
}

interface PaintToOrder {
  enabled: boolean;
  count: number;
  label: string;
  description: string;
}

interface DoorRange {
  productCategory: string;
  status: string;
  name: string;
  sku: string;
  description: string;
  category: string;
  subCategories?: string[];
  styleTags?: string[];
  finishType: string;
  priceGroup: string;
  thicknessMm: number | null;
  frameWidthMm: number | null;
  material: string;
  colours: Colour[];
  paintToOrder?: PaintToOrder;
  portfolioImages: PortfolioImage[];
  heroImage: string;
  heroImageColour?: string;
  doorCloseUps: PortfolioImage[];
  doorCloseUp?: string;
  specs: Record<string, string>;
  supplierId?: string;
  supplierName?: string;
  /* Legacy alt-spelling some older records may carry */
  priceBracket?: string;
}

interface RangesResponse {
  ranges?: DoorRange[];
}

// ── HTML escape (matches source's esc()) ────────────────────────────────────

function esc(s: unknown): string {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

// ── Eight categories (verbatim from source line 32907) ──────────────────────

const CATEGORIES = [
  "All",
  "Doors",
  "Handles",
  "Sinks",
  "Taps",
  "Worktops",
  "Storage Solutions",
  "Appliances",
  "Lighting",
] as const;

// ── Filter state ────────────────────────────────────────────────────────────

interface Filters {
  category: string;
  supplier: string;
  price: string;
  finish: string;
  availability: string;
  q: string;
}

function makeInitialFilters(): Filters {
  return {
    category: "All",
    supplier: "",
    price: "",
    finish: "",
    availability: "",
    q: "",
  };
}

// ── Page-section markup (verbatim from source lines 2638–2663) ──────────────

const PAGE_MARKUP = `
<div class="page-section" id="page-product-search">
  <div style="padding:18px 22px 8px;display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:12px">
    <div>
      <h2 style="font-size:20px;font-weight:800;color:var(--text);margin-bottom:6px">Product Search</h2>
      <p style="font-size:12px;color:var(--text2)">Browse the entire supplier catalogue -- doors, handles, sinks, taps, worktops, storage, appliances and lighting.</p>
    </div>
    <div id="ps-result-count" style="font-size:12px;color:#888"></div>
  </div>
  <!-- Search bar + filter pills -->
  <div style="padding:0 22px 12px;display:flex;flex-direction:column;gap:10px">
    <input type="search" id="ps-search" placeholder="Search by product name, SKU, colour, supplier…" style="width:100%;background:#1a1a1f;border:1px solid #2a2a2f;color:#eee;border-radius:10px;padding:12px 14px;font-size:13px;outline:none"/>
    <div id="ps-cat-pills" style="display:flex;flex-wrap:wrap;gap:6px"></div>
    <div style="display:flex;flex-wrap:wrap;gap:8px;align-items:center">
      <select id="ps-supplier" style="background:#1a1a1f;border:1px solid #2a2a2f;color:#eee;border-radius:8px;padding:8px 10px;font-size:12px;outline:none"><option value="">All suppliers</option></select>
      <select id="ps-price" style="background:#1a1a1f;border:1px solid #2a2a2f;color:#eee;border-radius:8px;padding:8px 10px;font-size:12px;outline:none"><option value="">Any price</option><option>£</option><option>££</option><option>£££</option><option>££££</option><option>£££££</option></select>
      <select id="ps-finish" style="background:#1a1a1f;border:1px solid #2a2a2f;color:#eee;border-radius:8px;padding:8px 10px;font-size:12px;outline:none"><option value="">Any finish</option></select>
      <select id="ps-availability" style="background:#1a1a1f;border:1px solid #2a2a2f;color:#eee;border-radius:8px;padding:8px 10px;font-size:12px;outline:none"><option value="">Any availability</option><option>Ex-Stock</option><option>Made to Order</option></select>
      <button id="ps-reset" style="background:transparent;border:1px solid #2a2a2f;color:#aaa;border-radius:8px;padding:8px 12px;font-size:12px;cursor:pointer">Reset filters</button>
    </div>
  </div>
  <!-- Results grid -->
  <div id="ps-results" style="padding:0 22px 28px;display:grid;grid-template-columns:repeat(auto-fill,minmax(280px,1fr));gap:14px">
    <div style="grid-column:1/-1;text-align:center;padding:60px 20px;color:#666">Loading catalogue…</div>
  </div>
</div>
`;

// ── Page ────────────────────────────────────────────────────────────────────

const productSearchPage: Page = {
  name: "product-search",
  label: "Product Search",
  icon: "🔎",

  mount(container: HTMLElement, ctx: PageContext): PageCleanup {
    container.innerHTML = PAGE_MARKUP;
    const root = container.querySelector<HTMLElement>("#page-product-search")!;

    let allProducts: DoorRange[] = [];
    let loaded = false;
    let disposed = false;
    const filters: Filters = makeInitialFilters();
    const ac = new AbortController();

    // Track detail overlays appended to <body> so cleanup can remove them.
    const overlays = new Set<HTMLElement>();

    // ── Element refs ─────────────────────────────────────────────────────
    const $ = <T extends HTMLElement = HTMLElement>(id: string): T | null =>
      root.querySelector<T>(`#${id}`);

    const pillsRoot = $("ps-cat-pills");
    const searchInput = $<HTMLInputElement>("ps-search");
    const supplierSel = $<HTMLSelectElement>("ps-supplier");
    const priceSel = $<HTMLSelectElement>("ps-price");
    const finishSel = $<HTMLSelectElement>("ps-finish");
    const availSel = $<HTMLSelectElement>("ps-availability");
    const resetBtn = $<HTMLButtonElement>("ps-reset");
    const resultsEl = $("ps-results");
    const countEl = $("ps-result-count");

    // ── renderProductSearch (verbatim port of source lines 32958–33032) ──
    function renderProductSearch(): void {
      if (disposed || !resultsEl) return;

      const q = searchInput?.value ?? "";
      filters.q = q.toLowerCase().trim();
      filters.supplier = supplierSel?.value ?? "";
      filters.price = priceSel?.value ?? "";
      filters.finish = finishSel?.value ?? "";
      filters.availability = availSel?.value ?? "";

      const visible = allProducts.filter((p) => {
        const pc = p.productCategory || "Doors";
        if (filters.category !== "All" && pc !== filters.category) return false;
        if (filters.supplier && (p.supplierName ?? "") !== filters.supplier)
          return false;
        if (
          filters.price &&
          (p.priceGroup || p.priceBracket || "") !== filters.price
        )
          return false;
        if (filters.finish) {
          const f = p.specs?.["finishType"] || p.finishType || "";
          if (f !== filters.finish) return false;
        }
        if (filters.availability) {
          const colours = p.colours ?? [];
          const hasAv = colours.some((c) => {
            if (filters.availability === "Ex-Stock")
              return c.availability === "Ex-Stock" || c.availability === "Stocked";
            return c.availability === filters.availability;
          });
          if (!hasAv && colours.length) return false;
        }
        if (filters.q) {
          const blob = [
            p.name,
            p.sku,
            p.description,
            p.supplierName,
            p.category,
            (p.colours ?? []).map((c) => c.name).join(" "),
          ]
            .join(" ")
            .toLowerCase();
          if (blob.indexOf(filters.q) === -1) return false;
        }
        return true;
      });

      if (countEl) {
        countEl.textContent =
          visible.length + " product" + (visible.length === 1 ? "" : "s");
      }

      if (!visible.length) {
        resultsEl.innerHTML =
          '<div style="grid-column:1/-1;text-align:center;padding:60px 20px;color:#666">No products match these filters.</div>';
        return;
      }

      resultsEl.innerHTML = visible
        .map((p, idx) => {
          const img =
            p.heroImage || p.portfolioImages?.[0]?.url || "";
          const pc = p.productCategory || "Doors";
          const pg = p.priceGroup || p.priceBracket || "";
          const col = (p.colours ?? []).length;
          const av = (p.colours ?? []).some(
            (c) => c.availability === "Ex-Stock" || c.availability === "Stocked",
          )
            ? "Ex-Stock"
            : "Made to Order";
          return (
            '<div class="ps-card" data-idx="' +
            idx +
            '">' +
            '<div class="ps-img"' +
            (img ? ' style="background-image:url(' + esc(img) + ')"' : "") +
            ">" +
            '<span class="ps-pcat">' +
            esc(pc) +
            "</span>" +
            (pg ? '<span class="ps-pg">' + esc(pg) + "</span>" : "") +
            "</div>" +
            '<div class="ps-body">' +
            '<div class="ps-name">' +
            esc(p.name) +
            "</div>" +
            '<div class="ps-sub">' +
            esc(p.supplierName || "Unknown supplier") +
            (p.category ? " • " + esc(p.category) : "") +
            "</div>" +
            (p.sku ? '<div class="ps-sku">SKU: ' + esc(p.sku) + "</div>" : "") +
            '<div class="ps-meta">' +
            '<span class="ps-tag">' +
            col +
            " colour" +
            (col === 1 ? "" : "s") +
            "</span>" +
            '<span class="ps-tag ' +
            (av === "Ex-Stock" ? "av-stock" : "av-mto") +
            '">' +
            av +
            "</span>" +
            "</div>" +
            "</div>" +
            "</div>"
          );
        })
        .join("");

      // Wire detail click (delegation via event listeners on each card —
      // matches source).
      resultsEl.querySelectorAll<HTMLElement>(".ps-card").forEach((card) => {
        card.addEventListener("click", () => {
          const idx = parseInt(card.dataset["idx"] ?? "-1", 10);
          const product = visible[idx];
          if (product) openProductDetail(product);
        });
      });
    }

    // ── resetProductSearch (verbatim port of source lines 33034–33045) ───
    function resetProductSearch(): void {
      filters.category = "All";
      filters.supplier = "";
      filters.price = "";
      filters.finish = "";
      filters.availability = "";
      filters.q = "";
      if (searchInput) searchInput.value = "";
      if (supplierSel) supplierSel.value = "";
      if (priceSel) priceSel.value = "";
      if (finishSel) finishSel.value = "";
      if (availSel) availSel.value = "";
      root.querySelectorAll<HTMLElement>("#ps-cat-pills .ps-pill").forEach((b) => {
        b.classList.toggle("active", b.dataset["cat"] === "All");
      });
      renderProductSearch();
    }

    // ── openProductDetail (verbatim port of source lines 33047–33112) ────
    function openProductDetail(p: DoorRange): void {
      const hero = p.heroImage || "";
      const allImgs: PortfolioImage[] = [];
      if (p.heroImage) allImgs.push({ url: p.heroImage, colourName: "" });
      (p.portfolioImages ?? []).forEach((i) => {
        allImgs.push(i);
      });
      (p.doorCloseUps ?? []).forEach((i) => {
        allImgs.push(i);
      });

      // Mirror legacy top-level fields onto specs (source lines 33055–33058).
      const specs: Record<string, string> = { ...(p.specs ?? {}) };
      (["material", "finishType", "thicknessMm", "frameWidthMm"] as const).forEach(
        (k) => {
          const v = p[k];
          if (v != null && v !== "" && specs[k] == null) {
            specs[k] = String(v);
          }
        },
      );

      const specRows = Object.keys(specs)
        .filter((k) => specs[k] !== "" && specs[k] != null)
        .map((k) => {
          const label = k
            .replace(/([A-Z])/g, " $1")
            .replace(/^./, (s) => s.toUpperCase());
          return (
            '<div class="k">' +
            esc(label) +
            '</div><div class="v">' +
            esc(specs[k]) +
            "</div>"
          );
        })
        .join("");

      const swatchHtml = (p.colours ?? [])
        .map((c) => {
          return (
            '<div class="ps-swatch">' +
            '<div class="sw" style="background:' +
            esc(c.hex || "#888") +
            '"></div>' +
            '<div class="nm">' +
            esc(c.name) +
            "</div>" +
            "</div>"
          );
        })
        .join("");

      const thumbs = allImgs
        .map((i) => {
          return (
            '<div class="ps-thumb" data-img="' +
            esc(i.url) +
            '" style="background-image:url(' +
            esc(i.url) +
            ')" title="' +
            esc(i.colourName || "") +
            '"></div>'
          );
        })
        .join("");

      const ov = document.createElement("div");
      ov.className = "ps-detail-ov";
      ov.innerHTML =
        '<div class="ps-detail">' +
        '<div class="ps-detail-h">' +
        "<h3>" +
        esc(p.name) +
        "</h3>" +
        '<button class="close-x" id="ps-close">&times;</button>' +
        "</div>" +
        '<div class="ps-detail-b">' +
        '<div class="ps-detail-row">' +
        "<div>" +
        '<div class="ps-hero-img" id="ps-hero" style="background-image:url(' +
        esc(hero) +
        ')"></div>' +
        (thumbs ? '<div class="ps-thumb-row">' + thumbs + "</div>" : "") +
        "</div>" +
        "<div>" +
        '<div style="display:flex;flex-wrap:wrap;gap:8px;align-items:center;margin-bottom:10px">' +
        '<span class="ps-tag" style="background:rgba(245,158,11,.15);color:#e58200;border-color:rgba(245,158,11,.3);font-weight:700">' +
        esc(p.productCategory || "Doors") +
        "</span>" +
        (p.category ? '<span class="ps-tag">' + esc(p.category) + "</span>" : "") +
        (p.priceGroup
          ? '<span class="ps-tag" style="font-family:monospace;font-weight:700;color:#fff">' +
            esc(p.priceGroup) +
            "</span>"
          : "") +
        "</div>" +
        '<div style="font-size:13px;color:#aaa;margin-bottom:14px">' +
        esc(p.supplierName || "Unknown supplier") +
        (p.sku
          ? ' • SKU <span style="font-family:monospace;color:#fff">' +
            esc(p.sku) +
            "</span>"
          : "") +
        "</div>" +
        (p.description
          ? '<div style="font-size:13px;color:#ddd;line-height:1.55;margin-bottom:18px">' +
            esc(p.description) +
            "</div>"
          : "") +
        (specRows
          ? '<div class="ps-detail-section"><h4>Specifications</h4><div class="ps-spec-grid">' +
            specRows +
            "</div></div>"
          : "") +
        (swatchHtml
          ? '<div class="ps-detail-section"><h4>Available colours / finishes</h4><div class="ps-swatch-grid">' +
            swatchHtml +
            "</div></div>"
          : "") +
        "</div>" +
        "</div>" +
        "</div>" +
        "</div>";

      document.body.appendChild(ov);
      overlays.add(ov);

      const closeOverlay = (): void => {
        ov.remove();
        overlays.delete(ov);
      };

      ov.querySelector<HTMLButtonElement>("#ps-close")?.addEventListener(
        "click",
        closeOverlay,
      );
      ov.addEventListener("click", (e) => {
        if (e.target === ov) closeOverlay();
      });
      ov.querySelectorAll<HTMLElement>(".ps-thumb").forEach((t) => {
        t.addEventListener("click", () => {
          const hero = ov.querySelector<HTMLElement>("#ps-hero");
          const url = t.dataset["img"];
          if (hero && url) {
            hero.style.backgroundImage = "url(" + url + ")";
          }
        });
      });
    }

    // ── Build category pills (verbatim from source lines 32915–32927) ────
    if (pillsRoot) {
      pillsRoot.innerHTML = CATEGORIES.map((c) => {
        return (
          '<button type="button" class="ps-pill' +
          (c === "All" ? " active" : "") +
          '" data-cat="' +
          esc(c) +
          '">' +
          esc(c) +
          "</button>"
        );
      }).join("");
      pillsRoot.querySelectorAll<HTMLButtonElement>(".ps-pill").forEach((btn) => {
        btn.addEventListener("click", () => {
          filters.category = btn.dataset["cat"] ?? "All";
          pillsRoot
            .querySelectorAll<HTMLButtonElement>(".ps-pill")
            .forEach((b) => {
              b.classList.toggle(
                "active",
                b.dataset["cat"] === filters.category,
              );
            });
          renderProductSearch();
        });
      });
    }

    // ── Wire input/select/button events (replace source's on* attrs) ─────
    searchInput?.addEventListener("input", renderProductSearch);
    supplierSel?.addEventListener("change", renderProductSearch);
    priceSel?.addEventListener("change", renderProductSearch);
    finishSel?.addEventListener("change", renderProductSearch);
    availSel?.addEventListener("change", renderProductSearch);
    resetBtn?.addEventListener("click", resetProductSearch);

    // ── loadProductSearch (verbatim port of source lines 32911–32956) ────
    async function loadProductSearch(): Promise<void> {
      if (loaded) {
        renderProductSearch();
        return;
      }
      loaded = true;

      try {
        const data = await ctx.api<RangesResponse>(
          "/door-ranges?status=approved",
          { method: "GET", signal: ac.signal },
        );
        if (disposed) return;
        allProducts = data.ranges ?? [];

        // Populate supplier dropdown (unique sorted values)
        if (supplierSel) {
          const suppliers = Array.from(
            new Set(
              allProducts
                .map((p) => p.supplierName ?? "")
                .filter((s): s is string => Boolean(s)),
            ),
          ).sort();
          suppliers.forEach((s) => {
            const o = document.createElement("option");
            o.value = s;
            o.textContent = s;
            supplierSel.appendChild(o);
          });
        }

        // Populate finish dropdown (unique sorted values)
        if (finishSel) {
          const finishes = Array.from(
            new Set(
              allProducts
                .map((p) => p.specs?.["finishType"] || p.finishType || "")
                .filter((s): s is string => Boolean(s)),
            ),
          ).sort();
          finishes.forEach((f) => {
            const o = document.createElement("option");
            o.value = f;
            o.textContent = f;
            finishSel.appendChild(o);
          });
        }

        renderProductSearch();
      } catch (err) {
        if (disposed) return;
        // Aborted by cleanup — silent.
        if (err instanceof DOMException && err.name === "AbortError") return;
        if (resultsEl) {
          const msg = err instanceof Error ? err.message : "unknown";
          resultsEl.innerHTML =
            '<div style="grid-column:1/-1;text-align:center;padding:60px 20px;color:#ef4444">Failed to load: ' +
            esc(msg) +
            "</div>";
        }
      }
    }

    // Kick off the fetch (source's data-onshow="loadProductSearch" hook).
    void loadProductSearch();

    return (): void => {
      disposed = true;
      ac.abort();
      // Source appends detail modals to <body> — clean any still open.
      for (const ov of overlays) ov.remove();
      overlays.clear();
    };
  },
};

registerPage(productSearchPage);

export default productSearchPage;
